import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2, CheckCircle2, ShieldCheck, CalendarClock, MapPin,
  Users, Store, AlertTriangle, CalendarPlus, Download, FileDown,
  ThumbsUp, CalendarRange, Send, Clock3, Sparkles,
} from "lucide-react";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DatesPicker } from "@/components/DatesPicker";
import { toast } from "sonner";

const LOGO_URL = "/shariti-logo.png";
const BRAND = "#EE722E";
const GREEN = "#1F9D55";

function frenchDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Lien « Ajouter au Google Agenda » (créneau 10h-20h) pour la 1re date.
function googleCalUrl(ack) {
  const iso = (ack?.iso_dates || [])[0];
  if (!iso) return null;
  const d = String(iso).slice(0, 10).replace(/-/g, "");
  const title = `${ack.association_acronym || ack.association_name || "Shariti"} — ${ack.spot_name || ""}`.trim();
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${d}T100000/${d}T200000`,
    location: ack.address_full || ack.ville || "",
    details: `Intervention Shariti (créneau 10h-20h). Interlocuteur : ${ack.cdp_name || "Shariti"}.`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function Shell({ children, logoSrc }) {
  const [src, setSrc] = useState(logoSrc || LOGO_URL);
  useEffect(() => { setSrc(logoSrc || LOGO_URL); }, [logoSrc]);
  return (
    <div
      className="min-h-screen w-full px-4 py-8 sm:py-12"
      style={{ background: "#FFF7F1", fontFamily: "'Montserrat', system-ui, sans-serif" }}
    >
      <div className="mx-auto w-full max-w-[560px]">
        <div className="mb-5 flex items-center justify-center">
          <img src={src} onError={() => setSrc(LOGO_URL)} alt="Logo" className="h-16 w-auto" />
        </div>
        {children}
        <p className="mt-5 text-center text-[11px] text-[rgba(24,24,24,0.4)]">
          Cette page vous a été transmise par votre interlocuteur partenariats.
        </p>
      </div>
    </div>
  );
}

function Card({ children, tone = "brand", label }) {
  const header =
    tone === "success"
      ? { bg: "linear-gradient(120deg,#1F9D55,#38B36B 70%,#7BD39A)", label: label || "Réception confirmée", Icon: CheckCircle2 }
      : { bg: "linear-gradient(120deg,#EE722E,#F89F63 70%,#FCDB53)", label: label || "Accusé de réception", Icon: ShieldCheck };
  return (
    <div
      className="overflow-hidden rounded-3xl border border-[rgba(24,24,24,0.06)] bg-white"
      style={{ boxShadow: "0 12px 44px rgba(24,24,24,0.10)" }}
    >
      <div className="px-6 py-5 text-white" style={{ background: header.bg }}>
        <div className="flex items-center gap-2 text-sm font-semibold opacity-95">
          <header.Icon className="h-4 w-4" /> {header.label}
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#FFF3E9] text-[#B5470F]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-[rgba(24,24,24,0.5)]">{label}</div>
        <div className="text-[14px] font-medium text-[#181818]">{value}</div>
      </div>
    </div>
  );
}

// Barre de progression légère (gamification) : 3 étapes.
function ProgressSteps({ current }) {
  // current : 0 = proposition reçue, 1 = votre réponse, 2 = confirmé
  const steps = ["Proposition reçue", "Votre réponse", "Confirmation"];
  const pct = current >= 2 ? 100 : current === 1 ? 55 : 15;
  return (
    <div className="mb-5" data-testid="nego-progress">
      <div className="relative mx-1 mb-2 h-1.5 rounded-full bg-[rgba(24,24,24,0.08)]">
        <div
          className="absolute left-0 top-0 h-1.5 rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg,${BRAND},${GREEN})` }}
        />
      </div>
      <div className="flex items-center justify-between">
        {steps.map((s, i) => {
          const done = i < current || current >= 2;
          const active = i === current && current < 2;
          return (
            <div key={s} className="flex flex-1 flex-col items-center gap-1 text-center">
              <div
                className="grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold"
                style={{
                  background: done ? GREEN : active ? BRAND : "rgba(24,24,24,0.10)",
                  color: done || active ? "#fff" : "rgba(24,24,24,0.5)",
                }}
              >
                {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span
                className="text-[10px] font-semibold leading-tight"
                style={{ color: done || active ? "#181818" : "rgba(24,24,24,0.45)" }}
              >
                {s}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OpBlock({ ack, datesLabel = "Dates & créneau", dates }) {
  return (
    <div className="rounded-2xl p-4" style={{ background: "#FAF6F2" }}>
      <InfoRow icon={Store} label="Magasin" value={ack.spot_name} />
      <InfoRow icon={MapPin} label="Adresse" value={ack.address_full || ack.ville} />
      <InfoRow
        icon={CalendarClock}
        label={datesLabel}
        value={[dates || ack.dates, "de 10h à 20h (plage indicative)"].filter(Boolean).join(" · ")}
      />
      <InfoRow icon={Users} label="Association" value={ack.association_name || ack.association_acronym} />
    </div>
  );
}

// --- Vue de négociation (kind = proposition, non encore confirmé) -----------
function NegotiationView({ ack, token, onUpdated }) {
  const [mode, setMode] = useState("choose"); // choose | counter
  const [counterText, setCounterText] = useState("");
  const [counterIso, setCounterIso] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const isCountered = ack.status === "countered";
  const current = isCountered ? 1 : 1;

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await publicApi.post(`/ack/public/${token}/accept`, {});
      onUpdated({ ...ack, ...data, status: "confirmed" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Une erreur est survenue.");
    } finally {
      setBusy(false);
    }
  };

  const sendCounter = async () => {
    if (!counterText.trim() && counterIso.length === 0) {
      toast.error("Choisissez au moins une date."); return;
    }
    setBusy(true);
    try {
      const { data } = await publicApi.post(`/ack/public/${token}/counter`, {
        counter_dates: counterText.trim() || undefined,
        counter_iso_dates: counterIso.length ? counterIso : undefined,
        counter_note: note.trim() || undefined,
      });
      onUpdated({ ...ack, ...data, status: "countered" });
      toast.success("Vos dates ont bien été transmises.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Une erreur est survenue.");
    } finally {
      setBusy(false);
    }
  };

  // État : contre-proposition envoyée → en attente de Shariti
  if (isCountered) {
    return (
      <Card tone="brand" label="Proposition de dates">
        <ProgressSteps current={1} />
        <div className="text-center" data-testid="nego-countered">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#FFF3E9]">
            <Clock3 className="h-8 w-8 text-[#B5470F]" />
          </div>
          <h2 className="mt-3 text-xl font-extrabold text-[#181818]">Merci, c'est transmis&nbsp;!</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-[rgba(24,24,24,0.65)]">
            Vos dates ont bien été envoyées à votre interlocuteur. Nous revenons vers vous très vite
            pour finaliser ensemble.
          </p>
        </div>
        <div className="mt-5 rounded-2xl border border-[rgba(238,114,46,0.25)] p-4" style={{ background: "#FFF7F1" }}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#B5470F]">
            Vos dates proposées
          </div>
          <div className="mt-1 text-[15px] font-bold text-[#181818]" data-testid="nego-counter-recap">
            {ack.counter_dates || "Dates transmises"}
          </div>
          {ack.counter_note && (
            <div className="mt-2 text-[13px] italic text-[rgba(24,24,24,0.65)]">« {ack.counter_note} »</div>
          )}
        </div>
        <div className="mt-5">
          <OpBlock ack={ack} datesLabel="Dates initialement proposées" />
        </div>
      </Card>
    );
  }

  // État : proposition en attente de la réponse du directeur
  return (
    <Card tone="brand" label="Proposition de dates">
      <ProgressSteps current={current} />
      <div className="text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#FFF3E9]">
          <CalendarRange className="h-8 w-8 text-[#B5470F]" />
        </div>
        <h2 className="mt-3 text-xl font-extrabold text-[#181818]">Choisissons la date qui vous arrange</h2>
        <p className="mx-auto mt-1 max-w-sm text-sm text-[rgba(24,24,24,0.65)]">
          Voici les dates que nous vous proposons. Validez-les en un clic, ou proposez-nous d'autres dates —
          c'est vous qui décidez&nbsp;!
        </p>
      </div>

      <div className="mt-5">
        <OpBlock ack={ack} datesLabel="Dates proposées" />
      </div>

      {mode === "choose" ? (
        <div className="mt-5 flex flex-col gap-2.5" data-testid="nego-actions">
          <button
            type="button"
            onClick={accept}
            disabled={busy}
            className="flex items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
            style={{ background: GREEN, boxShadow: "0 6px 18px rgba(31,157,85,0.30)" }}
            data-testid="nego-accept-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ThumbsUp className="h-4 w-4" /> Je valide ces dates</>}
          </button>
          <button
            type="button"
            onClick={() => setMode("counter")}
            disabled={busy}
            className="flex items-center justify-center gap-2 rounded-xl border border-[rgba(238,114,46,0.4)] bg-white px-4 py-3.5 text-sm font-bold text-[#8A3A10] transition-colors hover:bg-[#FFF3E9] disabled:opacity-60"
            data-testid="nego-counter-open-btn"
          >
            <CalendarRange className="h-4 w-4" /> Proposer d'autres dates
          </button>
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-[rgba(238,114,46,0.28)] p-4" style={{ background: "#FFF7F1" }} data-testid="nego-counter-panel">
          <div className="flex items-center gap-2 text-sm font-bold text-[#8A3A10]">
            <CalendarRange className="h-4 w-4" /> Proposez vos dates
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-[rgba(24,24,24,0.6)]">
            Sélectionnez une ou plusieurs dates sur le calendrier (ou saisissez-les librement).
          </p>
          <div className="mt-3">
            <DatesPicker
              value={counterText}
              onChange={setCounterText}
              onDatesChange={setCounterIso}
              label={null}
              testId="nego-dates"
              helperText="Cliquez sur l'icône calendrier pour choisir vos dates."
            />
          </div>
          <div className="mt-3">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Un mot pour votre interlocuteur (facultatif)…"
              rows={2}
              className="bg-white text-sm"
              data-testid="nego-note"
            />
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              onClick={sendCounter}
              disabled={busy}
              className="flex-1 text-white"
              style={{ background: BRAND }}
              data-testid="nego-counter-send-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Envoyer mes dates</>}
            </Button>
            <Button
              variant="outline"
              onClick={() => setMode("choose")}
              disabled={busy}
              className="border-[rgba(24,24,24,0.15)] bg-white"
              data-testid="nego-counter-cancel-btn"
            >
              Retour
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// --- Vue publique RENOUVELLEMENT ---------------------------------------------
// Déclenchée depuis le mail de remerciement : le responsable du magasin choisit
// LUI-MÊME les dates d'une prochaine venue. Ton chaleureux, sans engagement.
// Suggestion douce : ~60 jours (rythme moyen de renouvellement).
function RenouvellementView({ ack, token, onUpdated }) {
  const [text, setText] = useState("");
  const [iso, setIso] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const isDone = ack.status === "confirmed" || ack.status === "countered";

  // Des dates ont-elles DÉJÀ été proposées par Shariti dans l'email ? Si oui, on
  // permet au magasin de LES VALIDER en un clic (en plus d'en choisir d'autres).
  const hasProposed = (Array.isArray(ack.iso_dates) && ack.iso_dates.length > 0)
    || !!(ack.dates && (ack.dates + "").trim());
  const [showChoose, setShowChoose] = useState(!hasProposed);

  const suggestion = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 60);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  })();

  const send = async () => {
    if (!text.trim() && iso.length === 0) { toast.error("Choisissez au moins une date."); return; }
    setBusy(true);
    try {
      // Circuit LIBRE-SERVICE : le magasin choisit → l'accord est confirmé
      // automatiquement et les créneaux sont posés sur le planning.
      const { data } = await publicApi.post(`/ack/public/${token}/renew`, {
        dates: text.trim() || undefined,
        iso_dates: iso.length ? iso : undefined,
        note: note.trim() || undefined,
      });
      onUpdated({ ...ack, ...data, status: "confirmed" });
      toast.success("C'est confirmé ! Vos dates sont bien enregistrées.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Une erreur est survenue.");
    } finally { setBusy(false); }
  };

  // VALIDATION DIRECTE des dates proposées par Shariti (réutilise l'endpoint
  // public « accept » : finalise l'accord + attestation + placement au planning).
  const acceptProposed = async () => {
    setAccepting(true);
    try {
      const { data } = await publicApi.post(`/ack/public/${token}/accept`, {});
      onUpdated({ ...ack, ...data, status: "confirmed" });
      toast.success("Parfait, c'est validé ! Vos dates sont confirmées.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Une erreur est survenue.");
    } finally { setAccepting(false); }
  };

  if (isDone) {
    const recapDates = ack.dates || ack.counter_dates || "Dates transmises";
    return (
      <Card tone="brand" label="Vos prochaines dates">
        <div className="text-center" data-testid="renew-done">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[rgba(31,157,85,0.14)]">
            <Sparkles className="h-8 w-8 text-[#1F9D55]" />
          </div>
          <h2 className="mt-3 text-xl font-extrabold text-[#181818]">C'est confirmé, merci&nbsp;!</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-[rgba(24,24,24,0.65)]">
            Vos dates sont enregistrées et transmises à notre équipe&nbsp;: nous serons bien présents,
            avec le sourire. Un email de confirmation vient de vous être envoyé.
          </p>
        </div>
        <div className="mt-5 rounded-2xl border border-[rgba(31,157,85,0.28)] p-4" style={{ background: "rgba(31,157,85,0.06)" }}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#0F5A2F]">Vos prochaines dates confirmées</div>
          <div className="mt-1 text-[15px] font-bold text-[#181818]" data-testid="renew-recap">
            {recapDates}
          </div>
          {ack.counter_note && <div className="mt-2 text-[13px] italic text-[rgba(24,24,24,0.65)]">« {ack.counter_note} »</div>}
        </div>
      </Card>
    );
  }

  return (
    <Card tone="brand" label="Vos prochaines dates">
      <div className="text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#FFF3E9]">
          <CalendarRange className="h-8 w-8 text-[#B5470F]" />
        </div>
        <h2 className="mt-3 text-xl font-extrabold text-[#181818]">Au plaisir de vous revoir&nbsp;!</h2>
        <p className="mx-auto mt-1 max-w-sm text-sm text-[rgba(24,24,24,0.65)]">
          {hasProposed
            ? "Merci encore pour votre accueil. Voici les dates que nous vous proposons pour une prochaine venue : validez-les en un clic, ou choisissez-en d'autres — en toute liberté, sans aucun engagement."
            : "Merci encore pour votre accueil. Si le cœur vous en dit, choisissez les dates qui vous conviendraient pour une prochaine venue — en toute liberté, et sans aucun engagement."}
        </p>
      </div>

      {hasProposed && !showChoose ? (
        <>
          <div className="mt-4">
            <OpBlock ack={ack} datesLabel="Dates que nous vous proposons" />
          </div>
          <div className="mt-5 rounded-2xl border border-[rgba(238,114,46,0.28)] p-4" style={{ background: "#FFF7F1" }} data-testid="renew-accept-panel">
            <button type="button" onClick={acceptProposed} disabled={accepting}
              className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
              style={{ background: BRAND, boxShadow: "0 6px 18px rgba(238,114,46,0.30)" }}
              data-testid="renew-accept-btn">
              {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ThumbsUp className="h-4 w-4" /> Je valide ces dates</>}
            </button>
            <button type="button" onClick={() => setShowChoose(true)} disabled={accepting}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-[rgba(238,114,46,0.35)] bg-white px-4 py-3 text-sm font-bold text-[#8A3A10] transition-colors hover:bg-[#FFF3E9] disabled:opacity-60"
              data-testid="renew-prefer-other-btn">
              <CalendarRange className="h-4 w-4" /> Je préfère d'autres dates
            </button>
          </div>
        </>
      ) : (
        <div className="mt-5 rounded-2xl border border-[rgba(238,114,46,0.28)] p-4" style={{ background: "#FFF7F1" }} data-testid="renew-panel">
          <div className="flex items-center gap-2 text-sm font-bold text-[#8A3A10]">
            <CalendarRange className="h-4 w-4" /> Choisissez vos dates
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-[rgba(24,24,24,0.6)]">
            En général, nous revenons environ 60 jours après (soit vers le <b>{suggestion}</b>) — mais
            c'est vous qui choisissez ce qui vous arrange le mieux&nbsp;!
          </p>
          <div className="mt-3">
            <DatesPicker value={text} onChange={setText} onDatesChange={setIso}
              label={null} testId="renew-dates"
              helperText="Cliquez sur l'icône calendrier pour sélectionner vos dates." />
          </div>
          <div className="mt-3">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Un petit mot pour nous (facultatif)…" rows={2}
              className="bg-white text-sm" data-testid="renew-note" />
          </div>
          <button type="button" onClick={send} disabled={busy}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
            style={{ background: BRAND, boxShadow: "0 6px 18px rgba(238,114,46,0.30)" }}
            data-testid="renew-send-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Confirmer mes dates</>}
          </button>
          {hasProposed && (
            <button type="button" onClick={() => setShowChoose(false)} disabled={busy}
              className="mt-2 w-full text-center text-[12px] font-semibold text-[#8A3A10] underline-offset-2 hover:underline disabled:opacity-60"
              data-testid="renew-back-proposed-btn">
              ← Revenir aux dates proposées
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

// --- Écran de succès (confirmation classique OU proposition confirmée) -------
function ConfirmedView({ ack, token, isProposition }) {
  const gcal = googleCalUrl(ack);
  const icsUrl = (ack?.iso_dates || []).length
    ? `${publicApi.defaults.baseURL}/ack/public/${token}/ics`
    : null;
  const attestationUrl = `${publicApi.defaults.baseURL}/ack/public/${token}/attestation.pdf`;

  return (
    <Card tone="success">
      <div className="text-center" data-testid="ack-confirmed">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[rgba(31,157,85,0.14)]">
          <Sparkles className="h-8 w-8 text-[#1F9D55]" />
        </div>
        <h2 className="mt-3 text-xl font-extrabold text-[#181818]">
          {isProposition ? "Parfait, les dates sont validées !" : "Merci, c'est confirmé !"}
        </h2>
        <p className="mx-auto mt-1 max-w-sm text-sm text-[rgba(24,24,24,0.65)]">
          Votre accord a bien été enregistré. Merci sincèrement de nous accueillir —
          votre geste compte réellement. Un email de confirmation vient de vous être envoyé.
        </p>
      </div>

      <div className="mt-5"><OpBlock ack={ack} /></div>

      <div className="mt-5">
        <a
          href={attestationUrl} target="_blank" rel="noreferrer"
          className="flex items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-95"
          style={{ background: "#1F9D55", boxShadow: "0 6px 18px rgba(31,157,85,0.30)" }}
          data-testid="ack-attestation-btn"
        >
          <FileDown className="h-4 w-4" /> Télécharger l'attestation (PDF)
        </a>
        <p className="mt-2 text-center text-[11px] text-[rgba(24,24,24,0.5)]">
          Conservez ce justificatif : il peut être présenté à l'accueil du magasin le jour de l'intervention.
        </p>
      </div>

      {(gcal || icsUrl) && (
        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[rgba(24,24,24,0.5)]">
            <CalendarPlus className="h-3.5 w-3.5" /> Ajouter à votre agenda
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            {gcal && (
              <a
                href={gcal} target="_blank" rel="noreferrer"
                className="flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white transition-opacity hover:opacity-95"
                style={{ background: BRAND, boxShadow: "0 6px 18px rgba(238,114,46,0.30)" }}
                data-testid="ack-gcal-btn"
              >
                <CalendarPlus className="h-4 w-4" /> Google Agenda
              </a>
            )}
            {icsUrl && (
              <a
                href={icsUrl} target="_blank" rel="noreferrer"
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[rgba(24,24,24,0.14)] bg-white px-4 py-3 text-sm font-bold text-[#181818] transition-colors hover:bg-[#FAF6F2]"
                data-testid="ack-ics-btn"
              >
                <Download className="h-4 w-4" /> Autres agendas (.ics)
              </a>
            )}
          </div>
        </div>
      )}

      <p className="mt-5 text-center text-[11px] text-[rgba(24,24,24,0.45)]">
        Confirmation horodatée le {frenchDateTime(ack.confirmed_at)} — elle vaut accusé de réception.
      </p>
    </Card>
  );
}

export default function Confirmation() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [ack, setAck] = useState(null);
  const [error, setError] = useState(null);
  const confirmedOnce = useRef(false);

  // Chargement + auto-confirmation UNIQUEMENT pour les accusés « confirmation »
  // (1 clic). Les « propositions » n'auto-confirment jamais : le directeur choisit.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await publicApi.get(`/ack/public/${token}`);
      if (data.kind === "proposition" || data.kind === "renouvellement") {
        setAck(data);
      } else if (data.status === "confirmed") {
        setAck(data);
      } else if (!confirmedOnce.current) {
        confirmedOnce.current = true;
        const { data: res } = await publicApi.post(`/ack/public/${token}/confirm`, {});
        setAck({ ...data, ...res, status: "confirmed" });
      } else {
        setAck(data);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Lien introuvable ou expiré.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const assoLogo = ack?.association_id
    ? `${publicApi.defaults.baseURL}/assets/association-logo/${ack.association_id}`
    : null;

  if (loading) {
    return (
      <Shell>
        <div className="grid h-64 place-items-center rounded-3xl border border-[rgba(24,24,24,0.06)] bg-white">
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-7 w-7 animate-spin" style={{ color: BRAND }} />
            <p className="text-sm text-[rgba(24,24,24,0.6)]">Chargement…</p>
          </div>
        </div>
      </Shell>
    );
  }

  if (error && !ack) {
    return (
      <Shell>
        <div
          className="rounded-3xl border border-[rgba(214,69,69,0.25)] bg-white p-8 text-center"
          style={{ boxShadow: "0 12px 44px rgba(24,24,24,0.08)" }}
          data-testid="ack-error"
        >
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-[rgba(214,69,69,0.12)]">
            <AlertTriangle className="h-6 w-6 text-[#9B2226]" />
          </div>
          <h2 className="mt-3 text-lg font-bold text-[#181818]">Lien indisponible</h2>
          <p className="mt-1 text-sm text-[rgba(24,24,24,0.6)]">{error}</p>
        </div>
      </Shell>
    );
  }

  const isProposition = ack?.kind === "proposition";
  const isRenouvellement = ack?.kind === "renouvellement";

  // Renouvellement : le magasin choisit lui-même ses prochaines dates (ton bienveillant)
  if (ack && isRenouvellement) {
    return (
      <Shell logoSrc={assoLogo}>
        <RenouvellementView ack={ack} token={token} onUpdated={setAck} />
      </Shell>
    );
  }

  // Proposition en cours de négociation (proposed / countered)
  if (ack && isProposition && ack.status !== "confirmed") {
    return (
      <Shell logoSrc={assoLogo}>
        <NegotiationView ack={ack} token={token} onUpdated={setAck} />
      </Shell>
    );
  }

  // Confirmation classique OU proposition confirmée
  return (
    <Shell logoSrc={assoLogo}>
      <ConfirmedView ack={ack} token={token} isProposition={isProposition} />
    </Shell>
  );
}
